import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'

export default function Testimonials() {
  const { t } = useTranslation()
  const [testimonials, setTestimonials] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    loadTestimonials()
  }, [])

  useEffect(() => {
    // Auto-scroll carousel
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % testimonials.length)
      }, 5000) // Change every 5 seconds
      return () => clearInterval(interval)
    }
  }, [testimonials.length])

  const loadTestimonials = async () => {
    try {
      setLoading(true)

      // First, get featured testimonials from settings
      const settingsRef = doc(db, 'settings', 'testimonials')
      const settingsSnap = await getDoc(settingsRef)

      let selectedTestimonials = []

      if (settingsSnap.exists() && settingsSnap.data().selectedReviews?.length > 0) {
        // Load selected reviews
        const selectedIds = settingsSnap.data().selectedReviews.slice(0, 6) // Max 6

        // Load from COMMENTS collection with contentType='tour'
        const commentsQuery = query(
          collection(db, 'COMMENTS'),
          where('contentType', '==', 'tour'),
          where('featured', '==', true)
        )

        const snapshot = await getDocs(commentsQuery)
        selectedTestimonials = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(r => selectedIds.includes(r.id))
          .slice(0, 6)
      }

      // If no featured testimonials, get high-rated reviews
      if (selectedTestimonials.length === 0) {
        const commentsQuery = query(
          collection(db, 'COMMENTS'),
          where('contentType', '==', 'tour'),
          where('rating', '>=', 4)
        )

        const snapshot = await getDocs(commentsQuery)
        selectedTestimonials = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .sort((a, b) => (b.rating || 0) - (a.rating || 0))
          .slice(0, 6)
      }

      if (selectedTestimonials.length > 0) {
        // Enrich with tour names
        const toursSnapshot = await getDocs(collection(db, 'TOURS'))
        const toursMap = {}
        toursSnapshot.docs.forEach(doc => {
          toursMap[doc.id] = doc.data().name
        })

        const enrichedTestimonials = selectedTestimonials.map(t => ({
          ...t,
          name: t.userName || t.userEmail || 'Khách hàng',
          tour: t.tourTitle || toursMap[t.tourId] || 'Tour du lịch',
          location: 'Việt Nam',
          comment: t.comment || t.content || 'Trải nghiệm tuyệt vời!'
        }))

        setTestimonials(enrichedTestimonials)
      } else {
        // Fallback data
        setTestimonials([
          {
            name: 'Khách hàng hài lòng',
            avatar: '👤',
            location: 'Việt Nam',
            rating: 5,
            comment: 'Dịch vụ chuyên nghiệp, hướng dẫn viên nhiệt tình.',
            tour: 'Tour du lịch'
          }
        ])
      }
    } catch (error) {
      console.error('Error loading testimonials:', error)
      setTestimonials([
        {
          name: 'Khách hàng',
          avatar: '👤',
          location: 'Việt Nam',
          rating: 5,
          comment: 'Trải nghiệm tuyệt vời!',
          tour: 'Tour du lịch'
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <section className="testimonials">
        <div className="container">
          <h2>💬 {t('testimonials.title', 'Khách Hàng Nói Gì Về Chúng Tôi')}</h2>
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600"></div>
          </div>
        </div>
      </section>
    )
  }

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  const getVisibleTestimonials = () => {
    if (testimonials.length === 0) return []
    if (testimonials.length === 1) return testimonials
    if (testimonials.length === 2) {
      // Show 2 testimonials only
      const result = []
      for (let i = 0; i < 2; i++) {
        const index = (currentIndex + i) % testimonials.length
        result.push(testimonials[index])
      }
      return result
    }

    // Show 3 testimonials at a time on desktop, 1 on mobile
    const result = []
    const itemsToShow = Math.min(3, testimonials.length)
    for (let i = 0; i < itemsToShow; i++) {
      const index = (currentIndex + i) % testimonials.length
      result.push(testimonials[index])
    }
    return result
  }

  return (
    <section className="testimonials">
      <div className="container">
        <h2>💬 {t('testimonials.title', 'Khách Hàng Nói Gì Về Chúng Tôi')}</h2>
        <p className="subtitle">{t('testimonials.subtitle', 'Hàng ngàn khách hàng đã tin tưởng và hài lòng')}</p>

        <div className="carousel-container">
          <button className="carousel-btn prev" onClick={prevSlide} aria-label="Previous">
            ❮
          </button>

          <div className="testimonials-carousel">
            {getVisibleTestimonials().map((testimonial, index) => (
              <div key={testimonial.id || index} className="testimonial-card">
                <div className="testimonial-header">
                  <div className="avatar">
                    {testimonial.avatar || (testimonial.name ? testimonial.name.charAt(0).toUpperCase() : '👤')}
                  </div>
                  <div className="user-info">
                    <h4>{testimonial.name || 'Khách hàng'}</h4>
                    <p className="location">📍 {testimonial.location || 'Việt Nam'}</p>
                  </div>
                </div>

                <div className="rating">
                  {'⭐'.repeat(testimonial.rating || 5)}
                </div>

                <p className="comment">"{testimonial.comment || 'Dịch vụ tuyệt vời!'}"</p>

                <div className="tour-name">
                  🏖️ {testimonial.tour || 'Tour du lịch'}
                </div>
              </div>
            ))}
          </div>

          <button className="carousel-btn next" onClick={nextSlide} aria-label="Next">
            ❯
          </button>
        </div>

        <div className="carousel-dots">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <style>{`
        .testimonials {
          padding: 80px 24px;
          background: white;
          position: relative;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .testimonials h2 {
          text-align: center;
          font-size: 2.5rem;
          margin-bottom: 16px;
          color: #1f2937;
        }

        .subtitle {
          text-align: center;
          font-size: 1.2rem;
          color: #6b7280;
          margin-bottom: 48px;
        }

        .carousel-container {
          position: relative;
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .carousel-btn {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: white;
          border: 2px solid #667eea;
          color: #667eea;
          font-size: 1.5rem;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }

        .carousel-btn:hover {
          background: #667eea;
          color: white;
          transform: scale(1.1);
        }

        .testimonials-carousel {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
          overflow: hidden;
        }

        .testimonial-card {
          background: #f9fafb;
          padding: 24px;
          border-radius: 16px;
          border-left: 4px solid #667eea;
          transition: all 0.3s;
          animation: slideIn 0.5s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .testimonial-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .testimonial-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
        }

        .user-info h4 {
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 2px;
          font-size: 1rem;
        }

        .location {
          color: #6b7280;
          font-size: 0.85rem;
          margin: 0;
        }

        .rating {
          font-size: 1.1rem;
          margin-bottom: 12px;
        }

        .comment {
          color: #374151;
          line-height: 1.6;
          font-style: italic;
          margin-bottom: 12px;
          font-size: 0.95rem;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .tour-name {
          color: #667eea;
          font-weight: 600;
          font-size: 0.85rem;
        }

        .carousel-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 32px;
        }

        .dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #d1d5db;
          border: none;
          cursor: pointer;
          transition: all 0.3s;
        }

        .dot.active {
          background: #667eea;
          width: 24px;
          border-radius: 5px;
        }

        .dot:hover {
          background: #9ca3af;
        }

        @media (max-width: 1024px) {
          .testimonials-carousel {
            grid-template-columns: repeat(2, 1fr);
          }

          .testimonial-card:nth-child(3) {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .testimonials-carousel {
            grid-template-columns: 1fr;
          }

          .testimonial-card:nth-child(2),
          .testimonial-card:nth-child(3) {
            display: none;
          }

          .carousel-btn {
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
          }

          .testimonials h2 {
            font-size: 2rem;
          }
        }
      `}</style>
    </section>
  )
}

